
import { GameStats, getGrade, LEVELS } from './definitions';

export class UI {
  private startScreen: HTMLElement;
  private levelSelectScreen: HTMLElement;
  private gameScreen: HTMLElement;
  private resultsScreen: HTMLElement;

  private scoreEl: HTMLElement;
  private comboEl: HTMLElement;
  private levelEl: HTMLElement;
  private progressEl: HTMLElement;
  private wpmEl: HTMLElement;
  private timeEl: HTMLElement;

  private wordDisplayEl: HTMLElement;
  private inputDisplayEl: HTMLElement;
  private hiddenInput: HTMLInputElement;

  private onStartCallback: (() => void) | null = null;
  private onLevelSelectCallback: ((level: number) => void) | null = null;
  private onInputCallback: ((char: string) => void) | null = null;
  private onRestartCallback: (() => void) | null = null;
  private onMenuCallback: (() => void) | null = null;

  constructor() {
    this.createDOM();
    this.startScreen = document.getElementById('start-screen')!;
    this.levelSelectScreen = document.getElementById('level-select-screen')!;
    this.gameScreen = document.getElementById('game-screen')!;
    this.resultsScreen = document.getElementById('results-screen')!;

    this.scoreEl = document.getElementById('score-value')!;
    this.comboEl = document.getElementById('combo-value')!;
    this.levelEl = document.getElementById('level-value')!;
    this.progressEl = document.getElementById('progress-fill')!;
    this.wpmEl = document.getElementById('wpm-value')!;
    this.timeEl = document.getElementById('time-value')!;

    this.wordDisplayEl = document.getElementById('word-display')!;
    this.inputDisplayEl = document.getElementById('input-display')!;
    this.hiddenInput = document.getElementById('hidden-input') as HTMLInputElement;

    this.setupEventListeners();
  }

  private createDOM(): void {
    const container = document.getElementById('game-container')!;
    
    container.innerHTML = `
      <!-- Start Screen -->
      <div id="start-screen" class="screen active">
        <div class="screen-content">
          <h1 class="game-title">TYPE MASTER</h1>
          <p class="game-subtitle">打字练习挑战</p>
          <div class="btn-group">
            <button class="btn btn-primary" id="btn-start">开始游戏</button>
            <button class="btn btn-secondary" id="btn-levels">选择关卡</button>
          </div>
        </div>
      </div>

      <!-- Level Select Screen -->
      <div id="level-select-screen" class="screen">
        <div class="screen-content">
          <h2 class="game-title" style="font-size: 36px;">选择关卡</h2>
          <p class="level-desc">完成前一关卡解锁下一关</p>
          <div class="level-grid" id="level-grid"></div>
          <button class="btn btn-secondary" id="btn-back-start">返回</button>
        </div>
      </div>

      <!-- Game Screen -->
      <div id="game-screen" class="screen">
        <div class="game-header">
          <div class="stat-box">
            <span class="stat-label">分数</span>
            <span class="stat-value score" id="score-value">0</span>
          </div>
          <div class="stat-box">
            <span class="stat-label">连击</span>
            <span class="stat-value combo" id="combo-value">0</span>
          </div>
          <div class="level-progress">
            <div class="stat-box">
              <span class="stat-label">关卡</span>
              <span class="stat-value" id="level-value">1</span>
            </div>
            <div class="progress-bar">
              <div class="progress-fill" id="progress-fill" style="width: 0%"></div>
            </div>
          </div>
          <div class="stat-box">
            <span class="stat-label">速度</span>
            <span class="stat-value" id="wpm-value">0 WPM</span>
          </div>
          <div class="stat-box">
            <span class="stat-label">时间</span>
            <span class="stat-value" id="time-value">0:00</span>
          </div>
        </div>

        <div class="game-area">
          <div class="word-display">
            <div class="level-badge" id="level-badge">Level 1 - 入门训练</div>
            <div class="target-word" id="word-display"></div>
          </div>
          <div class="input-area">
            <input type="text" id="hidden-input" class="hidden-input" autocomplete="off" autocapitalize="off" autocorrect="off" spellcheck="false">
            <div class="input-display" id="input-display">点击此处开始输入</div>
            <p class="hint-text">输入上方显示的文字</p>
          </div>
        </div>
      </div>

      <!-- Results Screen -->
      <div id="results-screen" class="screen">
        <div class="screen-content">
          <h2 class="game-title" style="font-size: 36px;" id="result-title">关卡完成!</h2>
          <div class="result-grade" id="result-grade">A</div>
          <div class="results-stats">
            <div class="result-stat">
              <div class="result-stat-value" id="result-score">0</div>
              <div class="result-stat-label">总分</div>
            </div>
            <div class="result-stat">
              <div class="result-stat-value" id="result-accuracy">0%</div>
              <div class="result-stat-label">准确率</div>
            </div>
            <div class="result-stat">
              <div class="result-stat-value" id="result-wpm">0</div>
              <div class="result-stat-label">WPM</div>
            </div>
          </div>
          <div class="results-stats">
            <div class="result-stat">
              <div class="result-stat-value" id="result-combo">0</div>
              <div class="result-stat-label">最大连击</div>
            </div>
            <div class="result-stat">
              <div class="result-stat-value" id="result-words">0</div>
              <div class="result-stat-label">完成单词</div>
            </div>
            <div class="result-stat">
              <div class="result-stat-value" id="result-passed">--</div>
              <div class="result-stat-label">状态</div>
            </div>
          </div>
          <div class="btn-group">
            <button class="btn btn-primary" id="btn-restart">再来一次</button>
            <button class="btn btn-secondary" id="btn-next">下一关</button>
            <button class="btn btn-secondary" id="btn-menu">返回菜单</button>
          </div>
        </div>
      </div>
    `;
  }

  private setupEventListeners(): void {
    document.getElementById('btn-start')?.addEventListener('click', () => {
      this.onStartCallback?.();
    });

    document.getElementById('btn-levels')?.addEventListener('click', () => {
      this.onMenuCallback?.();
    });

    document.getElementById('btn-back-start')?.addEventListener('click', () => {
      this.showScreen('start');
    });

    document.getElementById('btn-restart')?.addEventListener('click', () => {
      this.onRestartCallback?.();
    });

    document.getElementById('btn-next')?.addEventListener('click', () => {
      const nextBtn = document.getElementById('btn-next') as HTMLElement;
      const nextLevel = parseInt(nextBtn.dataset.nextLevel || '1');
      this.onLevelSelectCallback?.(nextLevel);
    });

    document.getElementById('btn-menu')?.addEventListener('click', () => {
      this.onMenuCallback?.();
    });

    this.inputDisplayEl.addEventListener('click', () => {
      this.hiddenInput.focus();
    });

    this.hiddenInput.addEventListener('input', (e) => {
      const target = e.target as HTMLInputElement;
      const value = target.value;
      if (value.length > 0) {
        this.onInputCallback?.(value[value.length - 1]);
        target.value = '';
      }
    });

    document.addEventListener('keydown', (e) => {
      if (this.gameScreen.classList.contains('active') && !e.ctrlKey && !e.metaKey) {
        if (e.key.length === 1) {
          e.preventDefault();
          this.hiddenInput.focus();
          this.onInputCallback?.(e.key);
        }
      }
    });
  }

  showScreen(screen: 'start' | 'level-select' | 'game' | 'results'): void {
    [this.startScreen, this.levelSelectScreen, this.gameScreen, this.resultsScreen].forEach(s => {
      s.classList.remove('active');
    });

    switch (screen) {
      case 'start':
        this.startScreen.classList.add('active');
        break;
      case 'level-select':
        this.levelSelectScreen.classList.add('active');
        break;
      case 'game':
        this.gameScreen.classList.add('active');
        break;
      case 'results':
        this.resultsScreen.classList.add('active');
        break;
    }
  }

  showLevelSelect(unlockedLevels: number): void {
    this.showScreen('level-select');
    const grid = document.getElementById('level-grid')!;
    grid.innerHTML = '';

    LEVELS.forEach((level, index) => {
      const btn = document.createElement('button');
      btn.className = 'level-btn';
      btn.textContent = String(level.id);
      
      if (index + 1 <= unlockedLevels) {
        btn.classList.add(index + 1 < unlockedLevels ? 'completed' : 'current');
        btn.addEventListener('click', () => {
          this.onLevelSelectCallback?.(level.id);
        });
      } else {
        btn.disabled = true;
        btn.textContent = '🔒';
      }

      grid.appendChild(btn);
    });
  }

  updateStats(stats: GameStats): void {
    this.scoreEl.textContent = String(stats.score);
    this.comboEl.textContent = String(stats.combo);
    this.wpmEl.textContent = `${stats.wpm} WPM`;
  }

  updateProgress(progress: number): void {
    this.progressEl.style.width = `${progress * 100}%`;
  }

  updateTime(timeMs: number): void {
    const seconds = Math.floor(timeMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    this.timeEl.textContent = `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  updateLevel(level: number): void {
    this.levelEl.textContent = String(level);
    const levelData = LEVELS.find(l => l.id === level);
    if (levelData) {
      document.getElementById('level-badge')!.textContent = 
        `Level ${level} - ${levelData.name}`;
    }
  }

  displayWord(word: string, currentIndex: number): void {
    let html = '';
    for (let i = 0; i < word.length; i++) {
      let className = 'char ';
      if (i < currentIndex) {
        className += 'correct';
      } else if (i === currentIndex) {
        className += 'current';
      } else {
        className += 'pending';
      }

      const displayChar = word[i] === ' ' ? '&nbsp;' : word[i];
      html += `<span class="${className}">${displayChar}</span>`;
    }
    this.wordDisplayEl.innerHTML = html;
  }

  updateInputDisplay(input: string): void {
    this.inputDisplayEl.textContent = input || '输入中...';
  }

  showInputError(): void {
    this.inputDisplayEl.classList.add('error');
    setTimeout(() => {
      this.inputDisplayEl.classList.remove('error');
    }, 300);
  }

  showResults(stats: GameStats, level: number, passed: boolean): void {
    this.showScreen('results');
    
    const grade = getGrade(stats.accuracy);
    const gradeEl = document.getElementById('result-grade')!;
    gradeEl.textContent = grade;
    gradeEl.className = `result-grade grade-${grade.toLowerCase()}`;

    document.getElementById('result-score')!.textContent = String(stats.score);
    document.getElementById('result-accuracy')!.textContent = `${stats.accuracy.toFixed(1)}%`;
    document.getElementById('result-wpm')!.textContent = String(stats.wpm);
    document.getElementById('result-combo')!.textContent = String(stats.maxCombo);
    document.getElementById('result-words')!.textContent = String(stats.wordsCompleted);
    
    const passedEl = document.getElementById('result-passed')!;
    passedEl.textContent = passed ? '通关 ✓' : '未达标';
    passedEl.style.color = passed ? '#4ade80' : '#ff6b6b';

    const titleEl = document.getElementById('result-title')!;
    titleEl.textContent = passed ? '关卡完成!' : '再接再厉!';

    const nextBtn = document.getElementById('btn-next') as HTMLElement;
    if (passed && level < LEVELS.length) {
      nextBtn.style.display = 'block';
      nextBtn.dataset.nextLevel = String(level + 1);
    } else {
      nextBtn.style.display = 'none';
    }
  }

  showComboPopup(combo: number): void {
    const popup = document.createElement('div');
    popup.className = 'combo-popup';
    popup.textContent = `${combo}x COMBO!`;
    popup.style.left = `${50 + (Math.random() - 0.5) * 20}%`;
    popup.style.top = `${40 + (Math.random() - 0.5) * 10}%`;
    document.body.appendChild(popup);

    setTimeout(() => popup.remove(), 1000);
  }

  showScorePopup(score: number): void {
    const popup = document.createElement('div');
    popup.className = 'score-popup';
    popup.textContent = `+${score}`;
    popup.style.left = `${30 + (Math.random() - 0.5) * 20}%`;
    popup.style.top = `${50 + (Math.random() - 0.5) * 10}%`;
    document.body.appendChild(popup);

    setTimeout(() => popup.remove(), 800);
  }

  showPerfectEffect(): void {
    const effect = document.createElement('div');
    effect.className = 'perfect-effect';
    document.body.appendChild(effect);
    setTimeout(() => effect.remove(), 500);
  }

  onStart(callback: () => void): void {
    this.onStartCallback = callback;
  }

  onLevelSelect(callback: (level: number) => void): void {
    this.onLevelSelectCallback = callback;
  }

  onInput(callback: (char: string) => void): void {
    this.onInputCallback = callback;
  }

  onRestart(callback: () => void): void {
    this.onRestartCallback = callback;
  }

  onMenu(callback: () => void): void {
    this.onMenuCallback = callback;
  }

  focusInput(): void {
    this.hiddenInput.focus();
  }
}
